import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import useSWR from 'swr'
import { CircleCheck, CircleX, Clock, LoaderCircle } from 'lucide-react'
import { Badge, Switch, cn } from '@ludus/ui'
import type { PipelineId } from '@ludus/core'
import {
  FlowApiError,
  fetchFlow,
  listFlowRuns,
  setFlowEnabled,
  type FlowRun,
  type FlowSummary,
} from '@/lib/api/flows.ts'
import { apiErrorMessage } from '@/shared/feedback/apiErrorMessage.ts'
import { triggerOptionsOf } from './triggerNode.ts'

interface FlowCardProps {
  pipelineId: PipelineId
  flow: FlowSummary
  /** Status id → the name an operator gave it, for the trigger sentence. */
  statusNames: Map<string, string>
  /**
   * Re-read the catalogue after a write that changed this row.
   *
   * `void`, not `Promise`, for the reason `NewFlowDialog.onCreated` is: it runs
   * after the write has landed, so a failure to re-read is a stale list and
   * must not come back here as a failed toggle.
   */
  onChanged: () => void
}

/**
 * One flow in the catalogue: what it fires on, whether it is on, and how its
 * last run went.
 *
 * **Two reads per card, and they are deliberate.** `GET /flows` omits the graph
 * — it is the big column — and runs live on their own route, so neither the
 * trigger sentence nor the last-run strip can come out of the list. The detail
 * key is the one `FlowPage` reads, so opening a flow renders from cache rather
 * than fetching again. The scale this assumes is a handful of flows per
 * pipeline; a board with dozens would want a projection on the list route
 * rather than more fetches.
 */
export function FlowCard({ pipelineId, flow, statusNames, onChanged }: FlowCardProps) {
  const { t } = useTranslation()
  const [toggling, setToggling] = useState(false)
  const [toggleError, setToggleError] = useState<FlowApiError | null>(null)

  const detail = useSWR(`/pipelines/${pipelineId}/flows/${flow.id}`, () =>
    fetchFlow(pipelineId, flow.id),
  )
  const runs = useSWR(`/pipelines/${pipelineId}/flows/${flow.id}/runs?limit=1`, () =>
    listFlowRuns(pipelineId, flow.id, { limit: 1 }),
  )

  const statusName = (id: string): string => statusNames.get(id) ?? id

  function sentence(): string {
    if (detail.isLoading)
      return t('pages.pipelines.settings.flows.reading', { defaultValue: 'Reading the flow…' })
    if (detail.error != null)
      return t('pages.pipelines.settings.flows.unreadable', {
        defaultValue: 'This flow could not be read.',
      })

    const graph = detail.data?.flow.graph
    const trigger = graph ? triggerOptionsOf(graph) : null
    if (trigger === null)
      return t('pages.pipelines.settings.flows.triggerUnset', {
        defaultValue: 'No trigger set yet — open it to choose a stage.',
      })

    return trigger.fromStatusId === null
      ? t('pages.pipelines.settings.flows.trigger', {
          status: statusName(trigger.statusId),
          defaultValue: 'When a deal enters «{{status}}».',
        })
      : t('pages.pipelines.settings.flows.triggerFrom', {
          status: statusName(trigger.statusId),
          from: statusName(trigger.fromStatusId),
          defaultValue: 'When a deal enters «{{status}}» from «{{from}}».',
        })
  }

  async function toggle(next: boolean): Promise<void> {
    setToggling(true)
    setToggleError(null)
    try {
      await setFlowEnabled(pipelineId, flow.id, next)
      onChanged()
    } catch (err) {
      const e =
        err instanceof FlowApiError
          ? err
          : new FlowApiError({ kind: 'unknown', status: null, message: 'toggle failed' })
      setToggleError(e)
      // The row moved under us between the read and the write, so the switch is
      // showing a value nobody holds any more — re-read rather than leave it.
      if (e.kind === 'conflict') onChanged()
    } finally {
      setToggling(false)
    }
  }

  function toggleMessage(e: FlowApiError): string {
    if (e.kind === 'flow_invalid')
      return t('pages.pipelines.settings.flows.enableRefused', {
        defaultValue: 'Could not enable — this flow is not ready to run.',
      })
    if (e.kind === 'conflict')
      return t('pages.pipelines.settings.flows.changedElsewhere', {
        defaultValue: 'This flow changed elsewhere; the list has been re-read.',
      })
    return apiErrorMessage(e, t, 'pages.pipelines.settings.flows')
  }

  return (
    <li className="@container flex flex-col gap-2 rounded-lg border border-border/60 bg-background-secondary/40 p-3">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <div className="flex items-center gap-2">
            <Link
              to={`/pipelines/${pipelineId}/flows/${flow.id}`}
              className="truncate text-xs font-semibold text-foreground transition-colors hover:text-primary"
            >
              {flow.name}
            </Link>
            <Badge tone={flow.enabled ? 'primary' : 'muted'} size="sm">
              {flow.enabled
                ? t('pages.pipelines.settings.flows.on', { defaultValue: 'On' })
                : t('pages.pipelines.settings.flows.off', { defaultValue: 'Off' })}
            </Badge>
          </div>
          <p className="truncate text-xs text-muted">{sentence()}</p>
        </div>

        <Switch
          checked={flow.enabled}
          disabled={toggling}
          onCheckedChange={(next) => void toggle(next)}
          aria-label={t('pages.pipelines.settings.flows.enableLabel', {
            name: flow.name,
            defaultValue: 'Enable {{name}}',
          })}
        />
      </div>

      <LastRunStrip run={runs.data?.items[0] ?? null} loading={runs.isLoading} />

      {toggleError && (
        <p role="alert" className="text-[11px] font-medium text-toast-error-text">
          {toggleMessage(toggleError)}
        </p>
      )}
    </li>
  )
}

const RUN_ICON = {
  running: LoaderCircle,
  done: CircleCheck,
  failed: CircleX,
} as const

function LastRunStrip({ run, loading }: { run: FlowRun | null; loading: boolean }) {
  const { t, i18n } = useTranslation()

  if (loading) return null
  if (run === null) {
    return (
      <p className="flex items-center gap-1.5 border-t border-border/50 pt-2 text-[11px] text-muted">
        <Clock className="h-3 w-3 shrink-0" aria-hidden="true" />
        {t('pages.pipelines.settings.flows.neverRun', { defaultValue: 'Never run' })}
      </p>
    )
  }

  const Icon = RUN_ICON[run.state]
  const at = run.finishedAt ?? run.createdAt

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-border/50 pt-2 text-[11px]">
      <span
        className={cn(
          'flex items-center gap-1.5',
          run.state === 'failed' ? 'text-toast-error-text' : 'text-muted',
        )}
      >
        <Icon
          className={cn('h-3 w-3 shrink-0', run.state === 'running' && 'animate-spin')}
          aria-hidden="true"
        />
        {t(`pages.pipelines.settings.flows.runState.${run.state}`, { defaultValue: run.state })}
      </span>
      {/* The active language, not the browser's: uk is the displayed default,
          so an unqualified `toLocaleString()` would set an en-US date beside
          Ukrainian words. `ActivityTab` does the same. */}
      <span className="text-muted">{at.toLocaleString(i18n.language)}</span>
      {/* The engine's own code, not a sentence — `flow_runs.error` is machine
          readable and shown verbatim, the posture `SourceSection` takes with
          `lastSyncError`. The step log that explains it is the runs panel's. */}
      {run.error !== null && <span className="font-mono text-toast-error-text">{run.error}</span>}
    </div>
  )
}
